/**
 * CartMilestones Web Component
 * - Updates progress bar UI instantly via AJAX
 * - Automatically adds/removes free gift via AJAX and re-renders sections (no page reload)
 * - Does NOT touch window.fetch, window.routes, or any other theme JS
 * - cart.js calls el.init(cartData) after every cart mutation — that is the only hook needed
 */
class CartMilestones extends HTMLElement {

  connectedCallback() {
    // On first page load: update UI from Liquid-rendered data-total, then check gift
    const total = parseFloat(this.getAttribute('data-total') || '0');
    this._updateUI(total);
    this._checkGift();
  }

  /**
   * Called by cart.js after every cart add / change / update.
   * cartData is the full cart JSON object returned by Shopify.
   */
  init(cartData) {
    if (!cartData) return;
    const subtotalPaise = cartData.items_subtotal_price;
    const totalRupees   = subtotalPaise / 100;
    const items         = cartData.items || [];
    const giftVariantId = (this.getAttribute('data-gift-variant') || '').trim() || '47292568731906';

    // Calculate eligible total for accurate progress bar display (exclude gift & custom products)
    let excludeTotal = 0;
    items.forEach(item => {
      const isAutoGift = item.properties && item.properties['_is_free_gift'] === 'true';
      const isCustom = item.properties && item.properties['_requires_customization'] === 'true';
      if (String(item.variant_id) === String(giftVariantId) && isAutoGift) {
        excludeTotal += (item.final_line_price || 0) / 100;
      } else if (isCustom) {
        excludeTotal += (item.final_line_price || 0) / 100;
      }
    });
    const nonGiftTotal = totalRupees - excludeTotal;

    // 1. Update progress bar immediately with eligible total
    this._updateUI(nonGiftTotal);

    // 2. Lock checkout while evaluating gift logic
    this._toggleCheckout(true);

    // 3. Handle gift logic using the raw total
    this._checkGiftWith(totalRupees, items);
  }

  /* ─── Gift orchestration ─────────────────────────────────────────── */

  async _checkGift() {
    // First-load: fetch cart fresh so we have items
    let cart;
    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      cart = await res.json();
    } catch (e) { return; }
    this._checkGiftWith(cart.items_subtotal_price / 100, cart.items || []);
  }

  _checkGiftWith(totalRupees, items) {
    if (this._busy) return;

    const giftVariantId = (this.getAttribute('data-gift-variant') || '').trim() || '47292568731906';
    const giftThreshold = parseFloat(this.getAttribute('data-gift-threshold')) || 1499;

    // Find gift items AND their 1-indexed line positions, also calculate total to exclude
    const giftLines = [];
    let excludeTotal = 0;
    items.forEach((item, idx) => {
      const isAutoGift = item.properties && item.properties['_is_free_gift'] === 'true';
      const isCustom = item.properties && item.properties['_requires_customization'] === 'true';
      if (String(item.variant_id) === String(giftVariantId) && isAutoGift) {
        giftLines.push({ ...item, lineIndex: idx + 1 });
        // Accumulate the gift item's price (final_line_price is in paise)
        excludeTotal += (item.final_line_price || 0) / 100;
      } else if (isCustom) {
        // Exclude custom products from threshold calculation
        excludeTotal += (item.final_line_price || 0) / 100;
      }
    });

    // CRITICAL: exclude the gift item's own price and custom products from the threshold check.
    const nonGiftTotal = totalRupees - excludeTotal;
    console.log('[CartMilestones] total:', totalRupees, '| excluded total:', excludeTotal, '| eligible total:', nonGiftTotal, '| threshold:', giftThreshold);

    if (nonGiftTotal >= giftThreshold) {
      if (giftLines.length === 0) {
        this._addGift(giftVariantId);
      } else if (giftLines[0].quantity > 1) {
        this._changeGift(giftLines[0].lineIndex, 1);
      } else {
        // Gift is correct, unlock checkout
        this._toggleCheckout(false);
      }
    } else {
      // Cart dropped below threshold
      if (giftLines.length > 0) {
        for (const item of giftLines) {
          this._changeGift(item.lineIndex, 0);
        }
      } else {
        // No gift to remove, unlock checkout
        this._toggleCheckout(false);
      }
    }
  }

  /* ─── Add gift ───────────────────────────────────────────────────── */

  _toggleCheckout(disabled) {
    const selectors = '[name="checkout"], [href="/checkout"], .button-checkout, .checkout-btn, .btn-checkout, .cart__checkout-button';
    document.querySelectorAll(selectors).forEach(btn => {
      if (disabled) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.pointerEvents = 'none';
        btn.style.opacity = '0.6';
      } else {
        btn.removeAttribute('disabled');
        btn.style.pointerEvents = '';
        btn.style.opacity = '';
      }
    });
  }

  async _addGift(variantId) {
    this._busy = true;
    this._toggleCheckout(true);
    try {
      const sections = this._sectionsToRender();
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1, properties: { '_is_free_gift': 'true' } }],
          sections,
          sections_url: window.location.pathname
        })
      });
      if (res.ok) {
        const data = await res.json();
        this._renderSections(data);
        // Re-fetch fresh cart to update progress bar with gift included
        const cartRes = await fetch('/cart.js', { cache: 'no-store' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          this._updateUI(cart.items_subtotal_price / 100);
        }
      }
    } catch (e) {
      console.error('[CartMilestones] add error', e);
    } finally {
      this._busy = false;
      this._toggleCheckout(false);
    }
  }

  /* ─── Remove / fix gift quantity ─────────────────────────────────── */

  async _changeGift(lineNumber, quantity) {
    this._busy = true;
    this._toggleCheckout(true);
    try {
      const sections = this._sectionsToRender();
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineNumber,
          quantity,
          sections,
          sections_url: window.location.pathname
        })
      });
      if (res.ok) {
        const data = await res.json();
        this._renderSections(data);
        // Re-fetch fresh cart to update progress bar after gift removal
        const cartRes = await fetch('/cart.js', { cache: 'no-store' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          this._updateUI(cart.items_subtotal_price / 100);
        }
      }
    } catch (e) {
      console.error('[CartMilestones] change error', e);
    } finally {
      this._busy = false;
      this._toggleCheckout(false);
    }
  }

  /* ─── Which Shopify sections to re-render ────────────────────────── */

  _sectionsToRender() {
    const sections = ['minicart-form']; // always re-render the drawer
    const mainCart = document.getElementById('main-cart-items');
    if (mainCart && mainCart.dataset.id) {
      sections.push(mainCart.dataset.id); // cart page section
    }
    return sections;
  }

  /* ─── Apply section HTML from Shopify response ───────────────────── */

  _renderSections(data) {
    if (!data.sections) return;

    // --- Minicart drawer ---
    const minicartEl = document.getElementById('minicart-form');
    if (minicartEl && data.sections['minicart-form']) {
      const parsed   = new DOMParser().parseFromString(data.sections['minicart-form'], 'text/html');
      const newInner = parsed.getElementById('minicart-form');
      if (newInner) {
        minicartEl.innerHTML = newInner.innerHTML;
        // Re-bind event listeners on the cart notification after innerHTML swap
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.cartAction === 'function') {
          cartNotification.cartAction();
        }
        if (typeof BlsLazyloadImg !== 'undefined') BlsLazyloadImg.init();
      }
    }

    // --- Cart page ---
    const mainCart = document.getElementById('main-cart-items');
    if (mainCart && mainCart.dataset.id && data.sections[mainCart.dataset.id]) {
      const parsed   = new DOMParser().parseFromString(data.sections[mainCart.dataset.id], 'text/html');
      const newInner = parsed.querySelector('.js-contents');
      const target   = mainCart.querySelector('.js-contents') || mainCart;
      if (newInner) target.innerHTML = newInner.innerHTML;
    }

    // --- Cart item count badge ---
    if (data.item_count !== undefined) {
      document.querySelectorAll('.cart-count').forEach(el => {
        el.innerHTML = el.classList.contains('cart-count-drawer')
          ? `(${data.item_count})`
          : (data.item_count > 100 ? '~' : data.item_count);
      });
    }

    // --- Header subtotal (if theme has header-total-price element) ---
    const hTotal = document.querySelector('header-total-price');
    if (hTotal && typeof hTotal.updateTotal === 'function') hTotal.updateTotal(data);
  }

  /* ─── Progress bar UI update ─────────────────────────────────────── */

  _updateUI(totalRupees) {
    const shippingThreshold = parseFloat(this.getAttribute('data-shipping-threshold')) || 799;
    const giftThreshold     = parseFloat(this.getAttribute('data-gift-threshold'))     || 1499;

    // Fill bar
    const fill = this.querySelector('.milestone-progress-fill');
    if (fill) fill.style.width = `${Math.min((totalRupees / giftThreshold) * 100, 100)}%`;

    // Message
    const msg = this.querySelector('.milestone-message');
    if (msg) {
      if (totalRupees < shippingThreshold) {
        msg.innerHTML = `You're ₹${Math.ceil(shippingThreshold - totalRupees)} away from Free Shipping`;
      } else if (totalRupees < giftThreshold) {
        msg.innerHTML = `Free Shipping unlocked! 🎉 You're ₹${Math.ceil(giftThreshold - totalRupees)} away from a Free Gift`;
      } else {
        msg.innerHTML = `You unlocked Free Shipping 🚚 + Free Gift 🎁`;
      }
    }

    // Shipping Marker
    const sm = this.querySelector('.shipping-marker .marker-circle');
    const smSvg = this.querySelector('.shipping-marker .marker-circle svg');
    if (sm) {
      const reached = totalRupees >= shippingThreshold;
      sm.style.borderColor = reached ? 'var(--color-success, #2a7a2a)' : '#ccc';
      if (smSvg) smSvg.style.fill = reached ? 'var(--color-success, #2a7a2a)' : '#ccc';
    }

    // Gift Marker
    const gm = this.querySelector('.gift-marker .marker-circle');
    const gmSvg = this.querySelector('.gift-marker .marker-circle svg');
    if (gm) {
      const reached = totalRupees >= giftThreshold;
      gm.style.borderColor = reached ? 'var(--color-success, #2a7a2a)' : '#ccc';
      if (gmSvg) gmSvg.style.fill = reached ? 'var(--color-success, #2a7a2a)' : '#ccc';
    }
  }
}

if (!customElements.get('cart-milestones')) {
  customElements.define('cart-milestones', CartMilestones);
}

// --- Custom "Buy Now" handler (PDP overrides) ---
document.addEventListener('click', function(e) {
  // Capture both our custom Buy Now button and the theme's sticky buy-now-trigger
  const buyBtn = e.target.closest('.custom-buy-now-btn') || e.target.closest('.buy-now-trigger');
  if (!buyBtn) return;
  
  e.preventDefault();
  e.stopImmediatePropagation();

  const formElement = buyBtn.closest('form') || document.querySelector('form[action="/cart/add"]');
  if (!formElement) return;

  // --- Validation Injection: Prevent bypassing personalization fields ---
  const productForm = buyBtn.closest('product-form') || 
                      formElement.closest('product-form') || 
                      document.querySelector(`product-form[data-section="${formElement.dataset.section || buyBtn.closest('[data-section]')?.dataset.section}"]`);
  
  if (productForm && typeof productForm.validateRequiredFields === 'function') {
    const validation = productForm.validateRequiredFields();
    if (!validation.valid) {
      // Show errors using the native ProductForm methods
      if (typeof productForm.handleErrorMessage === 'function') {
        productForm.handleErrorMessage(validation.errors.join(', '));
      }
      if (typeof productForm.handleErrorMessagePopup === 'function') {
        productForm.handleErrorMessagePopup(validation.errors.join(', '));
      }
      if (typeof productForm.scrollToFormAndShowErrors === 'function') {
        productForm.scrollToFormAndShowErrors(validation.missingFields);
      }
      return; // Stop here - validation failed
    }
  }
  // ----------------------------------------------------------------------

  const getVariantInput = formElement.querySelector('[name="id"]');
  const getQtyInput = formElement.querySelector('[name="quantity"]');

  
  if (!getVariantInput) return;
  const variantId = getVariantInput.value;
  const quantity = getQtyInput ? parseInt(getQtyInput.value) || 1 : 1;

  // Render visual loading state mimicking native theme components
  buyBtn.setAttribute('aria-disabled', 'true');
  buyBtn.classList.add('loading');
  const svgLoad = buyBtn.querySelector('.icon-load');
  if (svgLoad) {
    svgLoad.classList.remove('hidden', 'opacity-0');
  }

  // Fallback defaults that mirror theme
  let giftThreshold = 1499;
  let giftVariantId = '47292568731906';
  
  // Try to pull dynamic thresholds statically from the DOM's milstone element
  const milestoneEl = document.querySelector('cart-milestones');
  if (milestoneEl) {
    giftThreshold = parseFloat(milestoneEl.getAttribute('data-gift-threshold')) || 1499;
    giftVariantId = (milestoneEl.getAttribute('data-gift-variant') || '').trim() || '47292568731906';
  }

  // 1. Fetch variant to evaluate its specific price alone (Cart independent)
  fetch(`/variants/${variantId}.js`)
    .then(r => r.json())
    .then(variant => {
      const price = variant.price / 100; // parse cents to rupees
      const totalAmount = price * quantity;
      
      const itemsToAdd = [];
      
      // Prevent recursive logic if the bought item somehow IS the gift variant
      if (String(variantId) !== String(giftVariantId)) {
        itemsToAdd.push({
          id: variantId,
          quantity: quantity
        });
        
        // 2. Conditional: Add gift only if this singular PDP item value >= Threshold
        if (totalAmount >= giftThreshold && giftVariantId) {
          itemsToAdd.push({
            id: Number(giftVariantId),
            quantity: 1,
            properties: { '_is_free_gift': 'true' }
          });
        }
      } else {
        itemsToAdd.push({
          id: variantId,
          quantity: quantity
        });
      }

      // 3. Purge existing cart natively (Requirement: Buy Now clears cart)
      return fetch('/cart/clear.js', { method: 'POST' })
        .then(() => fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: itemsToAdd })
        }))
        // Force Shopify to synchronously generate a fresh session DB lock to prevent propagation delays
        .then(() => fetch('/cart.js', { cache: 'no-store' }))
        .then(() => {
          // 4. Force redirect directly to Checkout Sequence
          window.location.href = '/checkout';
        });
    })
    .catch(err => {
      console.error('[Buy Now] Flow Overwrite Error:', err);
      buyBtn.removeAttribute('aria-disabled');
      buyBtn.classList.remove('loading');
      if (svgLoad) {
        svgLoad.classList.add('hidden', 'opacity-0');
      }
    });
}, true);

// --- Global Checkout Safety Lock ---
// Intercepts all clicks on checkout paths if any milestone component is busy.
document.addEventListener('click', function(e) {
  const milestone = document.querySelector('cart-milestones');
  if (milestone && milestone._busy) {
    const isCheckoutAction = e.target.closest('[name="checkout"], [href*="/checkout"], .btn-checkout, .cart__checkout-button');
    if (isCheckoutAction) {
      console.warn('[CartMilestones] Blocking checkout click while gift logic is processing...');
      e.preventDefault();
      e.stopImmediatePropagation();
    }
  }
}, true); // Use capture phase to beat theme listeners
